import { calculateDeliveryRating } from '../../domain';
import type { MissionRef } from '../../domain/catalog/mission';
import type { DeliveryOutcome } from '../../domain/deliveryRun/delivery';
import type { GiantId } from '../../domain/ids/ids';
import { useAppStore } from './appStore';
import {
  getActiveLevelAttempt,
  markLevelAttemptTerminalSettled,
} from './levelRunSession';

/** Snapshot of runner HUD at the moment the level ends. */
export type LevelRunSnapshot = {
  elapsedSec: number;
  sparksCollected: number;
  sparksTotal: number;
  speedBoostersCollected: number;
  speedBoostersTotal: number;
  /** Obstacle hits (lives lost). */
  collisions: number;
};

export async function settleLevelRun(params: {
  giantId: GiantId;
  orderIndex: number;
  outcome: DeliveryOutcome | 'aborted';
  snapshot: LevelRunSnapshot;
}) {
  const mission: MissionRef = {
    giantId: params.giantId,
    orderIndex: params.orderIndex,
  };
  const rating = calculateDeliveryRating({
    collectedSpeedBoosters: params.snapshot.speedBoostersCollected,
    targetSpeedBoosterCount: params.snapshot.speedBoostersTotal,
    collectedSparks: params.snapshot.sparksCollected,
    targetSparkCount: Math.max(1, params.snapshot.sparksTotal),
    collisions: params.snapshot.collisions,
  });

  return useAppStore.getState().settleDelivery({
    mission,
    outcome: params.outcome,
    rating,
    sparksCollected: params.snapshot.sparksCollected,
    sparksTotal: params.snapshot.sparksTotal,
  });
}

/** Success / fail — terminal settle; marks attempt so abort is skipped later. */
export async function settleLevelRunTerminal(params: {
  giantId: GiantId;
  orderIndex: number;
  outcome: DeliveryOutcome | 'aborted';
  snapshot: LevelRunSnapshot;
}) {
  const result = await settleLevelRun(params);
  markLevelAttemptTerminalSettled();
  return result;
}

/** Quit mid-run (pause → home) without finishing. */
export async function settleActiveLevelAbortedIfNeeded(): Promise<void> {
  const attempt = getActiveLevelAttempt();
  if (attempt == null || attempt.terminalSettled) {
    return;
  }
  await settleLevelRun({
    giantId: attempt.giantId,
    orderIndex: attempt.orderIndex,
    outcome: 'aborted',
    snapshot: attempt.snapshot,
  });
  markLevelAttemptTerminalSettled();
}

/** Replay: charge pass if needed, then start a new attempt. */
export async function beginReplayDelivery(
  giantId: GiantId,
  orderIndex: number,
): Promise<boolean> {
  const result = await useAppStore
    .getState()
    .ensureAndBeginDelivery({ giantId, orderIndex });
  return result.ok;
}
